import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, Ban, ChevronDown, ChevronRight, Play, CheckCircle, XCircle, Loader } from 'lucide-react';
import { format } from 'date-fns';
import { useTenderReview } from '../contexts/TenderReviewContext';
import { useBookmarks } from '../contexts/BookmarkContext';
import { forgeAPI } from '../services/api';

// Phase definitions for progress display
const ANALYSIS_PHASES = [
  { id: 'phase0', name: 'Basic Info', shortName: 'Info' },
  { id: 'phase1', name: 'Qualification', shortName: 'Qualify' },
  { id: 'phase2', name: 'Arbitrage', shortName: 'Arbitrage' },
  { id: 'phase3', name: 'Sourcing', shortName: 'Sourcing' },
];

export const getPriceEstimation = (tender) => {
  const title = tender.title?.toLowerCase() || '';
  const tenderId = tender._id || tender.tender_id || tender.tenderId || tender.id || tender.reference_number || '';

  let hash = 0;
  for (let i = 0; i < tenderId.length; i++) {
    hash = ((hash << 5) - hash) + tenderId.charCodeAt(i);
    hash = hash & hash;
  }
  const normalizedHash = Math.abs(hash % 10000) / 10000;

  let baseAmount = 250000;
  let range = 500000;

  if (title.includes('infrastructure') || title.includes('construction')) {
    baseAmount = 2000000;
    range = 7000000;
  } else if (title.includes('software') || title.includes('it') || title.includes('digital')) {
    baseAmount = 1000000;
    range = 4000000;
  } else if (title.includes('consulting') || title.includes('advisory')) {
    baseAmount = 500000;
    range = 2000000;
  }

  return Math.floor(baseAmount + (normalizedHash * range));
};

export const formatCurrency = (amount) => {
  if (!amount) return 'TBD';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    notation: 'compact'
  }).format(amount);
};

const SavedTenderCard = ({ tender, isExpanded, onToggleExpand, analysisState, onRunAnalysis, onNotesChange, inForge = false, onEnterForge, onRemoveFromForge }) => {
  const {
    getTenderStatus,
    saveTender,
    unsaveTender,
    markNotApplicable,
    isSaved,
    isNotApplicable
  } = useTenderReview();
  const { addBookmark, removeBookmark, isBookmarked } = useBookmarks();
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const normalized = dateString.length === 10 ? dateString + 'T00:00:00' : dateString;
      return format(new Date(normalized), 'MMM do, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getDaysUntilClose = (closingDate) => {
    if (!closingDate) return null;
    try {
      const days = Math.ceil(
        (new Date(closingDate) - new Date()) / (1000 * 60 * 60 * 24)
      );
      return days;
    } catch {
      return null;
    }
  };

  const closingDate =
    tender.closing_date ||
    tender.closingDate ||
    tender.deadline ||
    tender.end_date ||
    tender.endDate ||
    tender.close_date ||
    tender.closeDate ||
    tender.bid_deadline ||
    tender.bidDeadline ||
    null;

  const daysUntilClose = getDaysUntilClose(closingDate);
  const getStatusInfo = () => {
    const esStatus = tender.status?.toLowerCase();

    if (daysUntilClose !== null && daysUntilClose <= 0) {
      if (esStatus === 'active') {
        return { text: 'Closing Date Passed', class: 'status-date-passed' };
      }
      return { text: 'Closed', class: 'status-closed' };
    }

    if (daysUntilClose !== null && daysUntilClose <= 3) {
      return { text: 'Closing Soon', class: 'status-closing-soon' };
    }

    if (daysUntilClose !== null) {
      return { text: 'Open', class: 'status-open' };
    }

    return { text: tender.status || 'Open', class: 'status-open' };
  };

  const statusInfo = getStatusInfo();

  const organizationName =
    tender.organization?.contracting_entity ||
    tender.organization?.name ||
    tender.contracting_entity ||
    tender.buyer_name ||
    tender.department ||
    tender.organization_name ||
    (typeof tender.organization === 'string' ? tender.organization : null) ||
    'Government of Canada';

  const getKeyIndicators = (tender) => {
    const indicators = [];

    if (tender['canadianMade-fabriquéCanadien'] === 'yes' || tender['canadianMade-fabriquéCanadien'] === 'true') {
      indicators.push({ text: 'Canadian Made', type: 'canadian' });
    }
    if (tender['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === 'true') {
      indicators.push({ text: 'Indigenous Preferred', type: 'indigenous' });
    }
    if (tender['experienceRequired-expérienceRequise'] === 'true') {
      indicators.push({ text: 'References Required', type: 'experience' });
    }

    const category = tender['masterCategory-catégoriePrincipale'] || tender.procurement_category || '';
    const title = tender.title?.toLowerCase() || '';

    if (indicators.length === 0) {
      if (category.toLowerCase().includes('it') || category.toLowerCase().includes('technology') ||
          title.includes('software') || title.includes('digital') || title.includes('cyber')) {
        indicators.push({ text: 'IT Services', type: 'category' });
      } else if (category.toLowerCase().includes('construction') || title.includes('construction')) {
        indicators.push({ text: 'Construction', type: 'category' });
      } else if (category.toLowerCase().includes('professional') || category.toLowerCase().includes('consulting')) {
        indicators.push({ text: 'Professional Services', type: 'category' });
      } else {
        indicators.push({ text: 'Open Competition', type: 'general' });
      }
    }

    return indicators;
  };

  const estimatedAmount = getPriceEstimation(tender);
  const keyIndicators = getKeyIndicators(tender);
  const tenderId = tender._id || tender.tender_id || tender.tenderId || tender.id || tender.reference_number;
  const referenceNumber = tenderId || 'N/A';

  // Unified saved check — bookmarked OR saved in review context
  const tenderIsSaved = isSaved(tenderId) || isBookmarked(tenderId);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (tenderIsSaved) {
      unsaveTender(tenderId);
      removeBookmark(tenderId);
    }
  };

  const handleNotApplicable = (e) => {
    e.preventDefault();
    e.stopPropagation();
    markNotApplicable(tender);
    if (isBookmarked(tenderId)) {
      removeBookmark(tenderId);
    }
  };

  const handleExpand = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand(tenderId);
  };

  // Bid recommendation badge
  const getBidRecBadge = () => {
    const rec = tender.bidRecommendation || tender.bid_recommendation;
    if (!rec) return null;
    const recUpper = rec.toUpperCase();
    const colors = {
      BID: { bg: '#dcfce7', color: '#16a34a' },
      CONSIDER: { bg: '#fef3c7', color: '#92400e' },
      PASS: { bg: '#fee2e2', color: '#dc2626' }
    };
    const style = colors[recUpper] || colors.CONSIDER;
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 700,
        background: style.bg,
        color: style.color,
        marginRight: '4px'
      }}>
        {recUpper}
      </span>
    );
  };

  // Analysis status display
  const getAnalysisStatusBadge = () => {
    if (!analysisState) {
      if (tender.analysisStatus === 'complete') {
        return <span className="analysis-status-badge complete">Complete</span>;
      }
      return <span className="analysis-status-badge ready">Ready</span>;
    }
    switch (analysisState.status) {
      case 'running':
        return <span className="analysis-status-badge running"><Loader size={12} className="spinning" /> Analyzing...</span>;
      case 'complete':
        return <span className="analysis-status-badge complete"><CheckCircle size={12} /> Complete</span>;
      case 'failed':
        return <span className="analysis-status-badge failed"><XCircle size={12} /> Failed</span>;
      default:
        return <span className="analysis-status-badge ready">Ready</span>;
    }
  };

  return (
    <div className={`tender-card-wrapper ${tenderIsSaved ? 'status-saved' : ''} ${isNotApplicable(tenderId) ? 'status-not-applicable' : ''}`}>
      <Link to={tender.analysisStatus === 'complete' || analysisState?.status === 'complete' ? `/responses/${encodeURIComponent(tenderId)}` : `/tender/${encodeURIComponent(tenderId)}`} className="tender-card">
        <div className="tender-card-header">
          <div className="header-left">
            <span className={`tender-status ${statusInfo.class}`}>
              {statusInfo.text}
            </span>
            <h3 className="tender-title">{tender.title || 'Untitled Tender'}</h3>
          </div>
          <div className="header-actions">
            {getBidRecBadge()}
            {getAnalysisStatusBadge()}
            <button
              className={`action-btn expand-btn ${isExpanded ? 'active' : ''}`}
              onClick={handleExpand}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            <button
              className={`action-btn save-btn ${tenderIsSaved ? 'active' : ''}`}
              onClick={handleSave}
              title="Save this tender"
            >
              <Star size={12} fill={tenderIsSaved ? 'currentColor' : 'none'} />
              Save
            </button>
            <button
              className={`action-btn not-applicable-btn ${isNotApplicable(tenderId) ? 'active' : ''}`}
              onClick={handleNotApplicable}
              title="Mark as not applicable"
            >
              <Ban size={12} />
              N/A
            </button>
          </div>
        </div>

        <div className="tender-highlights">
          <p className="highlight-field">
            <strong>Amount:</strong> {formatCurrency(estimatedAmount)} CAD
          </p>
          <p className="highlight-field">
            <strong>Closing Date:</strong> {formatDate(closingDate)}
          </p>
        </div>

        <div className="tender-details-grid">
          <div className="tender-left-column">
            <p><strong>Category:</strong> {tender.procurementCategory || tender['masterCategory-catégoriePrincipale'] || tender.procurement_category || 'Unknown'}</p>
            <p><strong>Key Indicators:</strong>
              <span className="indicators-inline">
                {keyIndicators.map((indicator, index) => (
                  <span key={index} className="indicator-reference">
                    {indicator.text}
                  </span>
                ))}
              </span>
            </p>
          </div>

          <div className="tender-right-column">
            <p><strong>Organization:</strong> {organizationName}</p>
            <p><strong>Reference:</strong> <span className="reference-number">{referenceNumber}</span></p>
          </div>
        </div>
      </Link>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="bookmark-expand-panel" onClick={(e) => e.stopPropagation()}>
          {/* Notes */}
          <div className="expand-section">
            <label className="expand-label">Notes</label>
            <textarea
              className="notes-textarea"
              placeholder="Add notes about this tender..."
              value={tender.notes || ''}
              onChange={(e) => onNotesChange(tenderId, e.target.value)}
              rows={3}
            />
          </div>

          {/* Upload */}
          <div className="expand-section">
            <label className="expand-label">Documents</label>
            <div className="expand-upload-row">
              <input
                type="file"
                multiple
                accept="application/pdf"
                id={`upload-${tenderId}`}
                className="upload-input-hidden"
                onChange={async (e) => {
                  const selected = Array.from(e.target.files || []);
                  if (selected.length === 0) return;
                  const formData = new FormData();
                  for (const f of selected) formData.append('files', f);
                  formData.append('tender_id', tenderId);
                  formData.append('platform_name', 'manual');
                  try {
                    const response = await fetch('/api/documents/upload', {
                      method: 'POST',
                      body: formData,
                    });
                    const data = await response.json().catch(() => ({}));
                    if (response.status === 200) {
                      alert(`Uploaded ${data.succeeded_count} file${data.succeeded_count !== 1 ? 's' : ''}.`);
                    } else if (response.status === 207) {
                      const fails = (data.failed || []).map((f) => `${f.filename}: ${f.error}`).join('\n');
                      alert(`Uploaded ${data.succeeded_count} of ${data.total}.\n\nFailed:\n${fails}`);
                    } else {
                      alert(`Upload failed: ${data.detail || data.error || response.statusText}`);
                    }
                  } catch (err) {
                    alert('Upload failed: ' + err.message);
                  }
                  e.target.value = '';
                }}
              />
              <label htmlFor={`upload-${tenderId}`} className="btn-upload-small">
                Upload PDFs
              </label>
            </div>
          </div>

          {/* Analysis */}
          <div className="expand-section">
            <label className="expand-label">AI Analysis</label>

            {/* Phase Progress Bar */}
            <div className="analysis-progress">
              {ANALYSIS_PHASES.map((phase, idx) => {
                const completedPhases = analysisState?.completedPhases || [];
                const currentPhase = analysisState?.currentPhase;
                const isComplete = completedPhases.includes(phase.id) || tender.analysisStatus === 'complete';
                const isCurrent = currentPhase === phase.id;
                const isPending = !isComplete && !isCurrent;

                return (
                  <div key={phase.id} className={`progress-step ${isComplete ? 'complete' : ''} ${isCurrent ? 'current' : ''} ${isPending ? 'pending' : ''}`}>
                    <div className="step-indicator">
                      {isComplete ? (
                        <CheckCircle size={16} />
                      ) : isCurrent ? (
                        <Loader size={16} className="spinning" />
                      ) : (
                        <span className="step-number">{idx + 1}</span>
                      )}
                    </div>
                    <span className="step-label">{phase.shortName}</span>
                  </div>
                );
              })}
            </div>

            {/* Action buttons */}
            <div className="analysis-actions">
              {(tender.analysisStatus !== 'complete' && analysisState?.status !== 'running' && analysisState?.status !== 'complete') && (
                <button
                  className="btn-run-analysis"
                  onClick={() => onRunAnalysis(tenderId, tender)}
                  disabled={analysisState?.status === 'running'}
                >
                  <Play size={14} />
                  Run Analysis
                </button>
              )}
              {(tender.analysisStatus === 'complete' || analysisState?.status === 'complete') && (
                <button
                  className="btn-view-response"
                  onClick={() => navigate(`/responses/${encodeURIComponent(tenderId)}`)}
                >
                  View Response
                </button>
              )}
              {/* Discovery → FORGE: only on analysed tenders */}
              {(tender.analysisStatus === 'complete' || analysisState?.status === 'complete') && (
                inForge ? (
                  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn-send-forge" title="In FORGE — open the cockpit"
                      onClick={() => navigate(`/forge?tender=${encodeURIComponent(tenderId)}`)}>
                      In FORGE ✓
                    </button>
                    <button className="btn-link-muted" title="Remove from FORGE"
                      onClick={() => onRemoveFromForge && onRemoveFromForge(tenderId)}>
                      Remove
                    </button>
                  </span>
                ) : (
                  <button
                    className="btn-send-forge"
                    onClick={() => { forgeAPI.enterForge(tenderId, tender.title || '').catch(() => {}); if (onEnterForge) onEnterForge(); navigate(`/forge?tender=${encodeURIComponent(tenderId)}`); }}
                  >
                    Send to FORGE →
                  </button>
                )
              )}
              {analysisState?.error && (
                <p className="analysis-error">{analysisState.error}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedTenderCard;
